import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Airplane, Plus, ArrowLeft, PencilSimple, Trash, MapPin, CalendarBlank, Users, CurrencyInr } from '@phosphor-icons/react';

import { TripCard, EmptyTripsState, StatusChip, fmtMoney, tripDays, daysUntil } from '@/components/trips/TripCard';
import TripEditModal from '@/components/trips/TripEditModal';
import ItineraryTab from '@/components/trips/ItineraryTab';
import BudgetTab from '@/components/trips/BudgetTab';
import PackingTab from '@/components/trips/PackingTab';

const SUB_TABS = [
  { id: 'itinerary', label: 'Itinerary' },
  { id: 'budget',    label: 'Budget' },
  { id: 'packing',   label: 'Packing' },
];

export default function TripsPage() {
  const { api } = useAuth();
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingTrip, setEditingTrip] = useState(null);
  const [activeTripId, setActiveTripId] = useState(null);
  const [activeTrip, setActiveTrip] = useState(null);
  const [subTab, setSubTab] = useState('itinerary');
  const [tmBusy, setTmBusy] = useState(false);

  const fetchTrips = useCallback(async () => {
    try {
      const r = await api.get('/trips');
      setTrips(r.data || []);
    } catch (_) { /* ignore */ }
    setLoading(false);
  }, [api]);

  const fetchActiveTrip = useCallback(async (id) => {
    if (!id) return;
    try {
      const r = await api.get(`/trips/${id}`);
      setActiveTrip(r.data);
    } catch (_) { /* ignore */ }
  }, [api]);

  useEffect(() => { fetchTrips(); }, [fetchTrips]);
  useEffect(() => { if (activeTripId) fetchActiveTrip(activeTripId); }, [activeTripId, fetchActiveTrip]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this trip and ALL its data (itinerary, budget, packing)?')) return;
    try {
      await api.delete(`/trips/${id}`);
      setActiveTripId(null);
      setActiveTrip(null);
      await fetchTrips();
    } catch (_) { /* ignore */ }
  };

  const handleToggleTravelMode = async () => {
    if (!activeTrip) return;
    setTmBusy(true);
    try {
      const r = await api.patch(`/trips/${activeTrip.trip_id}/travel-mode`, { active: !activeTrip.travel_mode_active });
      setActiveTrip(r.data);
      await fetchTrips();
    } catch (_) { /* ignore */ }
    setTmBusy(false);
  };

  // ---------- LIST VIEW ----------
  if (!activeTripId) {
    return (
      <div data-testid="trips-page" className="space-y-4 sm:space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
          <div>
            <p className="text-neutral-500 text-xs sm:text-sm font-medium">Travel Planner</p>
            <h1 className="font-heading text-xl sm:text-2xl md:text-3xl font-bold text-white tracking-tight mt-1">Trips</h1>
          </div>
          {trips.length > 0 && (
            <button onClick={() => setShowCreate(true)} data-testid="trips-create-btn"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-2xl bg-[#FF6B35] text-white text-sm font-bold hover:bg-[#E85D2A] btn-glow">
              <Plus size={14} weight="bold" /> New trip
            </button>
          )}
        </div>

        {loading ? (
          <div className="text-center py-20 text-sm text-neutral-500">Loading trips…</div>
        ) : trips.length === 0 ? (
          <EmptyTripsState onCreate={() => setShowCreate(true)} />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="trips-list">
            {trips.map(t => (
              <TripCard key={t.trip_id} trip={t} onOpen={() => { setActiveTripId(t.trip_id); setActiveTrip(t); setSubTab('itinerary'); }} />
            ))}
          </div>
        )}

        {showCreate && (
          <TripEditModal api={api}
            onClose={() => setShowCreate(false)}
            onSaved={async (created) => {
              setShowCreate(false);
              await fetchTrips();
              if (created?.trip_id) { setActiveTripId(created.trip_id); setActiveTrip(created); }
            }} />
        )}
      </div>
    );
  }

  // ---------- DETAIL VIEW ----------
  if (!activeTrip) {
    return <div className="text-center py-20 text-sm text-neutral-500">Loading trip…</div>;
  }

  const days = tripDays(activeTrip);
  const until = daysUntil(activeTrip.start_date);

  return (
    <div data-testid={`trip-detail-${activeTrip.trip_id}`} className="space-y-4 sm:space-y-6">
      <div>
        <button onClick={() => { setActiveTripId(null); setActiveTrip(null); }} data-testid="trip-back-btn"
          className="inline-flex items-center gap-1.5 text-[11px] text-neutral-400 hover:text-white mb-2">
          <ArrowLeft size={12} weight="bold" /> All trips
        </button>
        <div className="rounded-2xl p-4 sm:p-5"
          style={{
            background: activeTrip.travel_mode_active
              ? 'linear-gradient(135deg, rgba(34,197,94,0.12), rgba(34,197,94,0.04))'
              : 'rgba(255,255,255,0.03)',
            border: `1px solid ${activeTrip.travel_mode_active ? 'rgba(34,197,94,0.40)' : 'rgba(255,255,255,0.08)'}`,
          }}>
          <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
            <div className="flex items-center gap-3 min-w-0">
              <Airplane size={22} weight="fill" className="text-[#FF6B35] flex-shrink-0" />
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="text-lg sm:text-xl font-bold text-white">{activeTrip.title}</h2>
                  <StatusChip status={activeTrip.status} />
                </div>
                <p className="text-[12px] text-neutral-400 mt-1 flex items-center gap-1.5">
                  <MapPin size={11} weight="fill" className="text-[#FF6B35]" />
                  {activeTrip.destination}
                </p>
              </div>
            </div>
            <div className="flex gap-1.5 flex-wrap">
              <button onClick={handleToggleTravelMode} disabled={tmBusy} data-testid="travel-mode-toggle"
                title={activeTrip.travel_mode_active ? 'Currently on trip — turn off when home' : 'Turn on when leaving home'}
                className={`px-3 py-1.5 rounded-lg text-[11px] font-bold border transition-colors flex items-center gap-1.5 ${activeTrip.travel_mode_active
                  ? 'bg-emerald-500/20 text-emerald-200 border-emerald-500/40'
                  : 'bg-white/5 text-neutral-300 border-white/10 hover:bg-white/10'}`}>
                <Airplane size={11} weight={activeTrip.travel_mode_active ? 'fill' : 'regular'} />
                {tmBusy ? '…' : activeTrip.travel_mode_active ? 'On trip — turn off' : 'Activate travel mode'}
              </button>
              <button onClick={() => setEditingTrip(activeTrip)} data-testid="trip-edit-btn"
                className="px-3 py-1.5 rounded-lg bg-white/5 text-neutral-300 border border-white/10 text-[11px] font-bold hover:bg-white/10 flex items-center gap-1">
                <PencilSimple size={11} /> Edit
              </button>
              <button onClick={() => handleDelete(activeTrip.trip_id)} data-testid="trip-delete-btn"
                className="px-3 py-1.5 rounded-lg bg-red-500/10 text-red-300 border border-red-500/30 text-[11px] font-bold hover:bg-red-500/20 flex items-center gap-1">
                <Trash size={11} /> Delete
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
            <Mini icon={<CalendarBlank size={11} />} label="Duration" value={`${days} day${days === 1 ? '' : 's'}`} />
            <Mini icon={<Users size={11} />} label="Party" value={`${activeTrip.party_size} traveller${activeTrip.party_size === 1 ? '' : 's'}`} />
            <Mini icon={<CurrencyInr size={11} />} label="Budget" value={fmtMoney(activeTrip.budget_amount, activeTrip.currency)} />
            <Mini icon={<CalendarBlank size={11} />} label="When"
              value={until === null ? '—' : until < 0 ? `${Math.abs(until)}d ago` : until === 0 ? 'Today' : `${until}d to go`} />
          </div>

          {activeTrip.notes && (
            <p className="text-[12px] text-neutral-400 mt-3 italic">{activeTrip.notes}</p>
          )}
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-1 p-1 rounded-2xl overflow-x-auto" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
        {SUB_TABS.map(t => (
          <button key={t.id} onClick={() => setSubTab(t.id)} data-testid={`trip-subtab-${t.id}`}
            className={`px-3 sm:px-4 py-2.5 rounded-xl text-xs sm:text-sm font-semibold transition-all whitespace-nowrap flex-shrink-0 ${subTab === t.id ? 'bg-[#FF6B35]/15 text-[#FF6B35]' : 'text-neutral-400 hover:text-white hover:bg-white/5'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {subTab === 'itinerary' && <ItineraryTab api={api} trip={activeTrip} onTripUpdate={() => fetchActiveTrip(activeTripId)} />}
      {subTab === 'budget' && <BudgetTab api={api} trip={activeTrip} />}
      {subTab === 'packing' && <PackingTab api={api} trip={activeTrip} />}

      {editingTrip && (
        <TripEditModal api={api} trip={editingTrip}
          onClose={() => setEditingTrip(null)}
          onSaved={async (updated) => {
            setEditingTrip(null);
            await fetchTrips();
            if (updated) setActiveTrip(updated);
          }} />
      )}
    </div>
  );
}

function Mini({ icon, label, value }) {
  return (
    <div className="rounded-lg p-2.5" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}>
      <p className="text-[9px] uppercase tracking-wider text-neutral-500 flex items-center gap-1">{icon} {label}</p>
      <p className="text-sm font-bold text-white mt-0.5 truncate">{value}</p>
    </div>
  );
}
